import './index.scss';
import React, { useCallback, useEffect, useState, FocusEvent, useLayoutEffect } from 'react';
import { isNumber } from '../../util';
import { useDocReader, useDocReaderDispatch } from '../../store/docReaderContext';
import classNames from 'classnames';
import useSticky from '../../hooks/useSticky';
import { lang } from '@umu/ukit-lang';
import { isMobile } from '@umu/umu-ua';
import { isRTL } from '@umu/umu-util';
interface IProps {
    offsetTop?: number;
}
export const ToolBar = (props: IProps) => {
    const { offsetTop = 0 } = props;
    const [state] = useDocReader();
    const { screenState, scale, pageNum, totalPages, displayPageNum, isShowBar, loading, showOutlines } = state;
    const dispatch = useDocReaderDispatch();
    const [currentPage, setCurrentPage] = useState<string>(' - ');
    const [isInputting, setIsInputting] = useState<boolean>(false);
    const [toolbarLeft, setToolbarLeft] = useState<number>(0);
    const [toolbarWidth, setToolbarWidth] = useState<number | string>(0);
    const pageJumpInputRef = React.useRef<HTMLInputElement>(null);
    const toolbarRef = React.useRef<HTMLDivElement>(null);
    const isSticky = useSticky(toolbarRef, offsetTop);

    function getToolbarFatherPosition() {
        const toolbarFather = toolbarRef.current?.parentElement;
        if (!toolbarFather) {
            return { x: 0, width: 'auto' };
        }
        const rect = toolbarFather.getBoundingClientRect();
        return rect;
    }
    // 全屏时更新toolbar的位置
    useLayoutEffect(() => {
        setToolbarLeft(getToolbarFatherPosition().x);
        if (isSticky) {
            setToolbarWidth(getToolbarFatherPosition().width);
        } else {
            setToolbarWidth('auto');
        }
    }, [isSticky, screenState, showOutlines]);

    const onInputFocus = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            e.target.value = String(displayPageNum);
        },
        [displayPageNum],
    );
    const onInputBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            setIsInputting(false);
            if (pageNum.value === Number(e.target.value)) {
                return;
            }
            dispatch({ type: 'pageJump', pageNum: Number(e.target.value) });
        },
        [pageNum, totalPages],
    );
    const onInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                setIsInputting(false);
                e.preventDefault();
                e.stopPropagation();
                if (pageNum.value === Number(e.currentTarget.value)) {
                    return;
                }
                dispatch({ type: 'pageJump', pageNum: Number(e.currentTarget.value) });
            }
        },
        [pageNum, totalPages],
    );

    useEffect(() => {
        if (!displayPageNum || !totalPages) {
            return;
        }
        setCurrentPage(`${displayPageNum} / ${totalPages}`);
    }, [displayPageNum, totalPages]);

    useEffect(() => {
        if (pageNum.value !== displayPageNum) {
            dispatch({ type: 'updateDisplayPageNum', pageNum: pageNum.value });
        }
    }, [state.pageNum]);

    // 切换flipping mode后，等待页面渲染完毕自动跳转到pageNum
    useEffect(() => {
        if (state.isRendered) {
            dispatch({ type: 'pageJump', pageNum: pageNum.value });
        }
    }, [state.pageFlipMode, state.isRendered]);

    return (
        <div
            className="toolbar-wrapper"
            style={{
                height: isShowBar ? toolbarRef.current?.offsetHeight : '0px',
                transition: 'height 0.2s ease-in',
            }}
        >
            <div
                className={classNames({
                    'document-reader tool-bar': true,
                    'tool-bar-full-screen': screenState === 'full',
                    'hide': !isShowBar,
                    'loading': loading,
                })}
                style={
                    !isRTL() ? { left: toolbarLeft, width: toolbarWidth } : { right: toolbarLeft, width: toolbarWidth }
                }
                ref={toolbarRef}
            >
                <div className="tool-bar-fullscreen">
                    <div
                        className={`screen btn-${screenState}`}
                        onClick={() => dispatch({ type: 'fullScreenToggle' })}
                    />
                </div>
                <div className="middle" />
                <div className="tool-bar-zoom">
                    <span
                        className={`tool-bar-left tool-bar-small ${scale === 0.1 ? 'leastScale' : ''}`}
                        onClick={() => dispatch({ type: 'zoomOut' })}
                    />
                    {`${(scale * 100).toFixed()}%`}
                    <span className="tool-bar-right tool-bar-big" onClick={() => dispatch({ type: 'zoomIn' })} />
                </div>

                <div className="middle" />

                <div className="tool-bar-pageturing">
                    <span
                        className={`tool-bar-left prev ${
                            !isNumber(pageNum.value) || pageNum.value <= 1 ? 'first-page' : ''
                        }`}
                        onClick={() => {
                            dispatch({ type: 'pageJump', pageNum: displayPageNum - 1 });
                        }}
                    />
                    <div className={`page-num`}>
                        {isInputting && (
                            <input
                                className="current-page"
                                onBlur={onInputBlur}
                                onFocus={onInputFocus}
                                type="number"
                                ref={pageJumpInputRef}
                                autoFocus
                                onKeyDown={onInputKeyDown}
                            ></input>
                        )}
                        {!isInputting && (
                            <span
                                className="current-page"
                                onClick={() => {
                                    setIsInputting(true);
                                }}
                            >
                                {currentPage}
                            </span>
                        )}
                    </div>
                    <span
                        className={classNames({
                            'next-page': true,
                            'tool-bar-right': true,
                            'last-page': !isNumber(pageNum.value || 0) || (pageNum.value || 0) >= totalPages,
                        })}
                        onClick={() => {
                            dispatch({ type: 'pageJump', pageNum: displayPageNum + 1 });
                        }}
                    />
                </div>
                <div
                    className="tool-bar-flipping-mode"
                    style={{
                        justifyContent: isMobile ? 'flex-start' : 'flex-end',
                    }}
                >
                    <div
                        className={classNames({
                            'flipping-mode': true,
                            'hidden': screenState !== 'full',
                        })}
                    >
                        <div className="flipping-mode-context-menu">
                            <div className="flipping-mode-context-menu-title">{lang('请选择翻页方式')}</div>
                            <div
                                className={classNames({
                                    'flipping-mode-context-menu-item': true,
                                    'check': state.pageFlipMode === 'horizontal',
                                })}
                                onClick={() => dispatch({ type: 'flippingModeHorizontal' })}
                            >
                                {lang('左右翻页')}
                            </div>
                            <div
                                className={classNames({
                                    'flipping-mode-context-menu-item': true,
                                    'check': state.pageFlipMode === 'vertical',
                                })}
                                onClick={() => dispatch({ type: 'flippingModeVertical' })}
                            >
                                {lang('上下滚动')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
